import type { ReactNode } from "react";
import { getTheme } from "@/config/theme";
import { cx } from "@/components/cx";
import { BrandLockup, BrandPin } from "./brand-lockup";
import { SiteFooter, buildSiteFooterLegal } from "./site-footer";
import { SiteNav } from "./site-nav";
import "./site-chrome.css";

// SiteNav is a client component (active-tab via usePathname + Clerk sign-out);
// re-exported so existing importers of "@/components/site-chrome" keep working.
export { SiteNav };
// SiteFooter now lives in ./site-footer (the ONE footer, every page — FAIL-442
// follow-up); re-exported so existing `import { SiteFooter } from
// "@/components/site-chrome"` call sites keep working unchanged.
export { SiteFooter };
// BrandPin/BrandLockup moved to their own Clerk-free ./brand-lockup module (so
// the lean public map viewer/embed can reuse the mark without importing this
// Clerk-tainted module); re-exported so existing importers keep working.
export { BrandLockup, BrandPin };

/**
 * The global site chrome, a persistent header + footer that connects every
 * surface (pricing, auth, account, connectors, api, legal, support). Without
 * this, routes are orphaned islands reachable only by typing the URL.
 *
 * The header is the floating pill nav from the approved modern-warm pricing
 * concept: a centered translucent rounded bar (blur + hairline border + soft
 * shadow) holding the teal pin-square brand mark on the left and the nav plus
 * a filled pill CTA on the right. The header's outer strip is transparent so
 * the pill sits cleanly on any page background.
 *
 * Renders plain links with NO Clerk client components (`<UserButton>` etc.
 * would crash without the provider; keyless-safe). `signedIn` is passed IN
 * (not read from `headers()` here) so this component stays static-render-safe;
 * auth surfaces compute it via `getCurrentAccount` in their layout and pass
 * it, while public/static pages use the default signed-out nav. All strings
 * from the white-label config; token-only styling.
 */

/** The floating pill-nav header on its own — the brand pill + primary nav.
 *  Factored out of SiteChrome so surfaces that supply their own footer/body
 *  (the editor landing hero, which renders inside its own gradient) can reuse
 *  the exact pill treatment. Takes brand values as props so it works in a
 *  client component too (the editor can't call the server-only getTheme). */
export function SiteHeader({
  brandName,
  wordmark,
  signedIn = false,
}: {
  brandName: string;
  wordmark: { text: string; logoUrl: string | null };
  signedIn?: boolean;
}) {
  return (
    <header className={cx("pz-siteheader", signedIn && "pz-siteheader--static")}>
      <div className="pz-siteheader__pill">
        {/* Signed in, the brand mark returns to the app dashboard, not the
            marketing homepage — nobody in-app wants the marketing page. */}
        <BrandLockup brandName={brandName} wordmark={wordmark} href={signedIn ? "/my-maps/" : "/"} />
        <SiteNav signedIn={signedIn} clientAuth={signedIn} />
      </div>
    </header>
  );
}

/**
 * Full chrome (header + footer) wrapping a route group's content. `signedIn`
 * is passed in (default false) so this stays static-render-safe.
 *
 * `attribution` is a REQUIRED prop, not resolved internally like `brand`/
 * `wordmark` (via `getTheme()`) — it comes from `@/lib/map/server`
 * (`resolveMapAttribution`), which is Node-only (geo-core adapters) and must
 * never be imported here: `site-chrome.tsx` is also imported by the
 * `IngestEditor` CLIENT component (for `SiteHeader`), so anything this module
 * imports gets pulled into the browser bundle too. Each server layout that
 * renders `<SiteChrome>` computes `attribution` itself and passes it in.
 */
export function SiteChrome({
  children,
  signedIn = false,
  attribution,
}: {
  children: ReactNode;
  signedIn?: boolean;
  attribution: string;
}) {
  const { brand, wordmark } = getTheme();
  return (
    <div className="pz-site">
      <SiteHeader brandName={brand.brandName} wordmark={wordmark} signedIn={signedIn} />
      <div className="pz-site__body">{children}</div>
      <SiteFooter
        brandName={brand.brandName}
        wordmark={wordmark}
        attribution={attribution}
        legal={buildSiteFooterLegal(brand)}
        signedIn={signedIn}
        markUrl={brand.logoUrl}
      />
    </div>
  );
}
